import React, { FormEvent, useState } from "react";
import * as S from "./signUpModal.style.ts";
import { useAuth } from "../../context/AuthContext.tsx";
import Button from "../Button/Button.tsx";
import { useNavigate } from "react-router-dom";
import { trackError, trackEvent } from "../../functions/mixpanel.ts";
import ModalComponent from "../Modal/Modal.tsx";
import Input from "../Input/Input.tsx";
import { LoginType } from "../../types.ts";

interface SignUpModalProps {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  loginMode: LoginType;
}

const getAuthErrorMessage = (err: any) => {
  switch (err?.code) {
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect email or password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Password should be at least 6 characters.";
    default:
      return "Something went wrong. Please try again.";
  }
};

const SignUpModal = ({ isOpen, setIsOpen, loginMode }: SignUpModalProps) => {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<LoginType>(loginMode);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      if (mode === "signup") {
        await signUp(email, password);
        trackEvent("Sign Up");
      } else {
        await signIn(email, password);
        trackEvent("Sign In");
      }

      navigate("/onboarding");
    } catch (err) {
      trackError("Login - handleSubmit:", { result: err });
      setError(getAuthErrorMessage(err));
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={mode === "signup" ? "Create your account" : "Welcome back"}
    >
      <S.ModalWrapper>
        <S.Form onSubmit={handleSubmit}>
          <Input
            label="email"
            labelValue="Email"
            inputType="email"
            placeHolder="you@example.com"
            defaultValue={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="password"
            labelValue="Password"
            inputType="password"
            placeHolder="Password"
            defaultValue={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <S.ErrorText>{error}</S.ErrorText>}
          <Button
            type="submit"
            buttonSize="medium"
            disabled={isSubmitting || !email || !password}
          >
            {isSubmitting
              ? "Please wait..."
              : mode === "signup"
                ? "Create account"
                : "Sign in"}
          </Button>
          <button
            type="button"
            className="toggle"
            onClick={() => {
              setError("");
              setMode(mode === "signup" ? "signin" : "signup");
            }}
          >
            {mode === "signup"
              ? "Already have an account? Sign in"
              : "New here? Get started free"}
          </button>
        </S.Form>
      </S.ModalWrapper>
    </ModalComponent>
  );
};

export default SignUpModal;
